import {
  CheckCircleIcon,
  EllipsisHorizontalIcon,
} from '@heroicons/react/20/solid';
import { XMarkIcon } from '@heroicons/react/24/solid';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import clsx from 'clsx';
import React, { useState } from 'react';
import { Checkbox } from '../../../atoms/Checkbox/Checkbox';
import { benefitOptions } from './defaults';

const MAX_BENEFITS = 5;

interface ReadonlyBenefitsProps {
  benefits: string[];
}

export const ReadonlyBenefits: React.FC<ReadonlyBenefitsProps> = ({
  benefits,
}) => {
  return (
    <div className="flex flex-wrap justify-center gap-x-xl border-b border-t border-neutral-300 p-l">
      {benefits.map((benefit) => (
        <div key={benefit} className="flex items-center gap-s">
          <CheckCircleIcon className="size-l fill-neutral-900" />
          <div className="text-body1Light text-neutral-900">{benefit}</div>
        </div>
      ))}
    </div>
  );
};

interface EditableBenefitsProps {
  benefits: string[];
  setBenefits: (benefits: string[]) => void;
}

export const EditableBenefits: React.FC<EditableBenefitsProps> = ({
  benefits,
  setBenefits,
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const benefitsFull = benefits.length >= MAX_BENEFITS;

  return (
    <div className="group relative">
      <div className="peer border border-dashed border-transparent hover:border-red-medium group-hover:border-red-medium">
        <ReadonlyBenefits benefits={benefits} />
      </div>
      <div
        className={clsx(
          'absolute left-0 top-0 -translate-y-full bg-red-medium text-[9px] leading-[9px] text-white',
          {
            flex: dropdownOpen,
            'hidden hover:flex peer-hover:flex': !dropdownOpen,
          }
        )}
      >
        <div className="p-xs">Benefits</div>
        <DropdownMenu.Root
          onOpenChange={(open) => setDropdownOpen(open)}
          open={dropdownOpen}
          modal={false}
        >
          <DropdownMenu.Trigger asChild>
            <button
              aria-label="toggle benefits dropdown"
              className="p-xs hover:bg-black hover:bg-opacity-20"
            >
              <EllipsisHorizontalIcon className="size-s" />
            </button>
          </DropdownMenu.Trigger>

          <DropdownMenu.Portal>
            <DropdownMenu.Content
              align="start"
              className="z-20 flex h-[400px] flex-col overflow-hidden rounded-small bg-white shadow-lg"
            >
              <div className="flex justify-between gap-s bg-neutral-200 p-m">
                <div className="text-body1Light text-neutral-800">
                  <span className="text-body1Medium">Benefits.</span> Selected{' '}
                  {benefits.length} of {MAX_BENEFITS}
                </div>
                <button
                  aria-label="close benefits dropdown"
                  onClick={() => setDropdownOpen(false)}
                >
                  <XMarkIcon className="size-l" />
                </button>
              </div>
              <div className="overflow-y-auto">
                {benefitOptions.map((option) => {
                  const checked = benefits.includes(option);
                  const enabled = checked || !benefitsFull;
                  return (
                    <DropdownMenu.CheckboxItem
                      key={option}
                      disabled={!enabled}
                      checked={checked}
                      onCheckedChange={(newChecked) => {
                        if (newChecked && benefitsFull) {
                          return;
                        }

                        const newDraftBenefits = benefits.filter(
                          (benefit) => benefit !== option
                        );
                        if (newChecked) {
                          newDraftBenefits.push(option);
                        }
                        setBenefits(
                          benefitOptions.filter((o) =>
                            newDraftBenefits.includes(o)
                          )
                        );
                      }}
                      onSelect={(e) => e.preventDefault()}
                      className={clsx('p-xs', {
                        'cursor-pointer': enabled,
                      })}
                      aria-label={option}
                    >
                      <div
                        className={clsx(
                          'flex justify-between gap-s rounded-xsmall px-s py-[9px]',
                          {
                            'bg-blue-light': checked,
                          }
                        )}
                      >
                        <span
                          className={clsx('select-none text-body1Light', {
                            'text-brandMidnight': checked,
                            'text-neutral-800': !checked && !benefitsFull,
                            'text-neutral-600': !checked && benefitsFull,
                          })}
                        >
                          {option}
                        </span>
                        <Checkbox
                          aria-label={option}
                          size="small"
                          checked={checked}
                          disabled={!enabled}
                          onChange={() => {}}
                        />
                      </div>
                    </DropdownMenu.CheckboxItem>
                  );
                })}
              </div>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </div>
    </div>
  );
};
